<template>
  <!-- Dashboard is excluded: several of its children (DayView, WeekView, etc.)
       read currentUser/permissions once from localStorage at setup() time, not
       reactively. A kept-alive instance skips setup() on re-entry (onActivated
       fires instead of onMounted), so re-logging-in after a logout reused a
       stale cached instance and showed the previous session's state — blank
       panes, wrong admin/teacher lock — until a hard refresh. -->
  <router-view v-slot="{ Component }">
    <keep-alive exclude="Dashboard">
      <component :is="Component" />
    </keep-alive>
  </router-view>

  <!-- The workspace sidebar lives here so it survives route changes: whichever
       shell is on screen (AdminLayout, the wide Dashboard) claims it and it's
       teleported into that shell's column; with no host it waits, still
       mounted, in the hidden parking spot. See useAdminSidebar.js. -->
  <div id="workspace-sidebar-parking" hidden />
  <Teleport defer :to="`#workspace-sidebar-${adminSidebar.host.value || 'parking'}`">
    <WorkspaceSidebar />
  </Teleport>

  <!-- Global keyboard shortcuts live here, not in Dashboard (App.vue) — this
       is the one component that stays mounted across every route (admin
       panel, team calendar, dashboard), so it's the only place a shortcut
       listener won't go dead when you navigate away from the dashboard. -->
  <ShortcutsHelpModal v-if="showShortcutsHelp" :shortcuts="shortcutList()" @close="showShortcutsHelp = false" />
  <GlobalSearchModal v-if="globalSearch.isOpen.value && globalSearch.canUse()" v-bind="searchModalProps()" @close="globalSearch.close()" />
  <LessonCreateModal
    v-if="showCreateLessonModal"
    v-bind="{ ...lessonCreateProps(), ...createExtra }"
    @close="showCreateLessonModal = false"
    @saved="showCreateLessonModal = false"
  />
  <MembershipCreateModal
    v-if="showCreateMembershipModal"
    @close="showCreateMembershipModal = false"
    @saved="showCreateMembershipModal = false"
  />
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useDemoMode } from './composables/useDemoMode'
import { useKeyboardShortcuts } from './composables/useKeyboardShortcuts'
import { useShortcutsEnabled } from './composables/useShortcutsEnabled'
import { useSearchableViews } from './composables/useSearchableViews'
import { useActiveSearchFocus } from './composables/useActiveSearchFocus'
import { useActiveTeacherDropdown } from './composables/useActiveTeacherDropdown'
import { useActiveCalendarMode } from './composables/useActiveCalendarMode'
import { useGlobalSearch } from './composables/useGlobalSearch'
import { useAdminSidebar } from './composables/useAdminSidebar.js'
import { easternToday, localIso } from './composables/useCalendarData.js'
import ShortcutsHelpModal from './components/common/ShortcutsHelpModal.vue'
import GlobalSearchModal from './components/common/GlobalSearchModal.vue'
import LessonCreateModal from './components/common/LessonCreateModal.vue'
import MembershipCreateModal from './components/common/MembershipCreateModal.vue'
import WorkspaceSidebar from './components/admin/WorkspaceSidebar.vue'

const router = useRouter()
const demoMode = useDemoMode()
const activeSearchFocus = useActiveSearchFocus()
const activeTeacherDropdown = useActiveTeacherDropdown()
const activeCalendarMode = useActiveCalendarMode()
const globalSearch = useGlobalSearch()
const adminSidebar = useAdminSidebar()

// Deliberately plain functions, not computed refs: `user` lives in
// localStorage (see router/index.js's beforeEach, which reads it the same
// way), and AppRoot never remounts across login/logout, so a computed here
// would cache a stale value across a login. These are only ever called at
// keypress time or right before a render, so a fresh read is cheap and
// always correct.
function storedUser() {
  try { return JSON.parse(localStorage.getItem('user') || 'null') } catch { return null }
}
function currentPermissions() {
  return demoMode.permissionsFor(storedUser()?.permissions)
}
function hasSignupsPermission() {
  const perms = currentPermissions()
  return perms.includes('admin') || perms.includes('lesson_signups')
}

// The *real* logged-in user's admin flag, not the previewed teacher's — demo
// mode is admin-only, and "x" must still work while previewing a non-admin.
function isRealAdmin() {
  return (storedUser()?.permissions || []).includes('admin')
}
// Opens the header's user menu (same event the coach-mark tour uses) and
// flags DemoModePanel to expand and open its "Preview as" dropdown.
function openDemoPreview() {
  demoMode.previewPickerRequested.value = true
  window.dispatchEvent(new Event('tour:open-user-menu'))
}

const showShortcutsHelp = ref(false)
const showCreateLessonModal = ref(false)
const showCreateMembershipModal = ref(false)
// Opened in the workspace sidebar while a shell hosts it; elsewhere (the narrow
// dashboard) as popups.
const lessonCreateProps = () => ({
  initialDate:       localIso(easternToday()),
  lockedTeacherName: hasSignupsPermission() ? null : storedUser()?.name,
  lockedTeacherId:   hasSignupsPermission() ? null : storedUser()?.id,
  canManageSignups:  hasSignupsPermission(),
})
// Reschedule / start-again props (utils/stopReasons.js openReschedule), on top of the usual ones.
const createExtra = ref({})
function newLesson(extra = {}) {
  createExtra.value = extra
  if (adminSidebar.available.value) adminSidebar.open('lesson-create', { ...lessonCreateProps(), ...extra })
  else showCreateLessonModal.value = true
}
const onOpenLessonCreate = (e) => newLesson(e.detail || {})
onMounted(() => window.addEventListener('lesson-create:open', onOpenLessonCreate))
onUnmounted(() => window.removeEventListener('lesson-create:open', onOpenLessonCreate))
function newMembership() {
  if (adminSidebar.available.value) adminSidebar.open('membership-create')
  else showCreateMembershipModal.value = true
}
function toggleSignups() {
  adminSidebar.toggle('signups', { isAdmin: currentPermissions().includes('admin') })
}

const searchableViews = useSearchableViews(currentPermissions, () => storedUser()?.tenant_id)
// People search rides along only when the Directory itself is reachable —
// results link there, and GET /contacts/search has the same permission gate.
function searchModalProps() {
  const views = searchableViews()
  return { views, searchPeople: views.some(v => v.to === '/admin/contacts/directory') }
}

const allShortcuts = [
  { keys: ['?'], description: 'Show this help', run: () => { showShortcutsHelp.value = !showShortcutsHelp.value } },
  { keys: ['/'], description: 'Search views (press again to close)', run: () => { globalSearch.isOpen.value ? globalSearch.close() : globalSearch.open() }, when: globalSearch.canUse },
  { keys: ['n'], description: 'New lesson', run: () => newLesson() },
  { keys: ['m'], description: 'New membership offer', run: newMembership, when: () => currentPermissions().includes('admin') },
  { keys: ['u'], description: 'Lesson signups (press again to close)', run: toggleSignups, when: () => adminSidebar.available.value && hasSignupsPermission() },
  { keys: ['s'], get description() { return activeSearchFocus.label() }, run: () => activeSearchFocus.focus(), when: activeSearchFocus.isAvailable },
  { keys: ['t'], description: 'Open teacher filter', run: () => activeTeacherDropdown.open(), when: activeTeacherDropdown.isAvailable },
  { keys: ['c'], description: 'Clear calendar filters', run: () => activeTeacherDropdown.clear(), when: activeTeacherDropdown.canClear },
  { keys: ['1'], description: 'Calendar: week view', run: () => activeCalendarMode.setMode('day'), when: activeCalendarMode.isAvailable },
  { keys: ['2'], description: 'Calendar: month view', run: () => activeCalendarMode.setMode('month'), when: activeCalendarMode.isAvailable },
  { keys: ['d'], description: 'Demo mode: preview as…', run: () => { showShortcutsHelp.value = false; openDemoPreview() }, when: isRealAdmin },
  { keys: ['x'], description: 'Exit demo mode', run: () => { demoMode.exitDemoMode(); window.location.reload() }, when: () => isRealAdmin() && demoMode.active.value },
]

// Shortcuts whose `when` fails are dropped entirely — not just made a no-op —
// so admin-only actions (demo mode) don't show in the help overlay
// or fire for teachers who don't have those views. A plain function (called
// fresh in the template above), not a computed, for the same non-reactive-
// localStorage-read reason as storedUser()/currentPermissions() above.
function shortcutList() {
  return allShortcuts.filter(s => !s.when || s.when())
}

const { shortcutsEnabled } = useShortcutsEnabled()

// Blocked outright while one of this component's own create modals is up, or the
// user isn't logged in yet, so e.g. "n" while that modal is open just types
// "n". Modals owned by App.vue or an admin child view (Edit Profile,
// Preferences, a lesson-series editor, etc.) aren't visible from up here, so
// they rely on useKeyboardShortcuts' own isTypingTarget guard (focus in a
// field) rather than modal tracking — fine for the common case (a modal with
// an input focused), but a shortcut could in principle fire over a modal
// whose focus is on a non-input control. Revisit if that turns out to matter.
useKeyboardShortcuts(allShortcuts, {
  isBlocked: () => !shortcutsEnabled.value || !storedUser() || showCreateLessonModal.value || showCreateMembershipModal.value,
})

// Initialize theme from localStorage
onMounted(() => {
  const savedTheme = localStorage.getItem('theme')
  if (savedTheme === 'dark') {
    document.documentElement.classList.add('dark')
  } else if (savedTheme === 'light') {
    document.documentElement.classList.remove('dark')
  } else {
    // Default to system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }
})
</script>

<style>
/* Global styles */
body {
  font-family: 'Inter', sans-serif;
}
</style>